import { useEffect, useMemo, useRef, useState } from "react";
import { Pago, MedioPago } from "../store/cart";
import {
  useMpAvailability,
  useMpPointPayment,
  useMpQrPayment,
} from "../hooks/useMpPayment";

type Method = "efectivo" | "tarjeta" | "qr" | "mixto";
type TarjetaTipo = "debito" | "credito";
type QrModo = "qr" | "point";
export type TipoComprobante = "ticket" | "factura_b" | "whatsapp";

export interface ConfirmExtras {
  tipo_comprobante: TipoComprobante;
  imprimir: boolean;
}

interface PagoPanelProps {
  total: number;
  clienteLabel: string;
  itemsCount: number;
  onClose: () => void;
  onConfirm: (pagos: Pago[], extras: ConfirmExtras) => void;
}

interface PagoEntry extends Pago {
  id: string;
  label: string;
}

const SUGGEST_BILLS = [200, 500, 1000, 2000, 5000];
const CUOTAS: { q: number; sub: string }[] = [
  { q: 1, sub: "sin interés" },
  { q: 3, sub: "sin interés" },
  { q: 6, sub: "+12%" },
  { q: 9, sub: "+18%" },
  { q: 12, sub: "+25%" },
  { q: 18, sub: "+38%" },
];

function fmt(n: number): string {
  return (
    "$" + n.toLocaleString("es-AR", { maximumFractionDigits: 2 })
  );
}

function labelMedio(m: MedioPago): string {
  switch (m) {
    case "efectivo":
      return "Efectivo";
    case "debito":
      return "Débito";
    case "credito":
      return "Crédito";
    case "transferencia":
      return "Transferencia";
    case "cta_cte":
      return "Cta. Cte.";
    case "mp_qr":
      return "MP QR";
    case "mp_point":
      return "MP Point";
  }
}

// ---------- icons (subset of design's components.jsx) ----------
type IconName =
  | "back"
  | "banknote"
  | "card"
  | "qr"
  | "split"
  | "plus"
  | "minus"
  | "trash"
  | "arrow-right"
  | "phone"
  | "clipboard";

const Icon = ({
  name,
  size = 20,
  stroke = 1.8,
}: {
  name: IconName;
  size?: number;
  stroke?: number;
}) => {
  const common = {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: stroke,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
  };
  switch (name) {
    case "back":
      return (
        <svg {...common}>
          <path d="M15 6l-6 6 6 6" />
        </svg>
      );
    case "banknote":
      return (
        <svg {...common}>
          <rect x="2" y="6" width="20" height="12" rx="2" />
          <circle cx="12" cy="12" r="2.5" />
          <path d="M6 6v.01M18 18v.01" />
        </svg>
      );
    case "card":
      return (
        <svg {...common}>
          <rect x="2" y="5" width="20" height="14" rx="2.5" />
          <path d="M2 10h20M6 15h3" />
        </svg>
      );
    case "qr":
      return (
        <svg {...common}>
          <rect x="3" y="3" width="7" height="7" rx="1.5" />
          <rect x="14" y="3" width="7" height="7" rx="1.5" />
          <rect x="3" y="14" width="7" height="7" rx="1.5" />
          <path d="M14 14h3v3h-3zM20 14v3M14 20h3v1M20 20v1" />
        </svg>
      );
    case "split":
      return (
        <svg {...common}>
          <path d="M5 4v6a4 4 0 0 0 4 4h6a4 4 0 0 1 4 4v2M5 20l-2-2 2-2M19 20l2-2-2-2" />
        </svg>
      );
    case "plus":
      return (
        <svg {...common}>
          <path d="M12 5v14M5 12h14" />
        </svg>
      );
    case "minus":
      return (
        <svg {...common}>
          <path d="M5 12h14" />
        </svg>
      );
    case "trash":
      return (
        <svg {...common}>
          <path d="M4 7h16M10 11v6M14 11v6M6 7l1 12a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-12M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
        </svg>
      );
    case "arrow-right":
      return (
        <svg {...common}>
          <path d="M5 12h14M13 6l6 6-6 6" />
        </svg>
      );
    case "phone":
      return (
        <svg {...common}>
          <rect x="7" y="3" width="10" height="18" rx="2" />
          <path d="M11 18h2" />
        </svg>
      );
    case "clipboard":
      return (
        <svg {...common}>
          <rect x="5" y="4" width="14" height="17" rx="2.5" />
          <path d="M9 4h6v3H9z" />
          <path d="M9 12h6M9 16h4" />
        </svg>
      );
  }
};

// ---------- numpad ----------
const Numpad = ({
  onKey,
}: {
  onKey: (k: string) => void;
}) => {
  const keys = ["1", "2", "3", "C", "4", "5", "6", "←", "7", "8", "9", "%", ".", "0", "00", "OK"];
  return (
    <div className="grid grid-cols-4 gap-2.5">
      {keys.map((k) => {
        const isOK = k === "OK";
        const isClear = k === "C" || k === "←";
        return (
          <button
            key={k}
            type="button"
            onClick={() => onKey(k)}
            className={`h-14 rounded-2xl font-bold text-xl border ${
              isOK
                ? "bg-[var(--mint)] text-white border-transparent shadow-[0_6px_14px_-6px_rgba(31,200,155,0.55)]"
                : isClear
                  ? "bg-[var(--bg-sunken)] text-[var(--ink)] border-transparent"
                  : "bg-white text-[var(--ink)] border-[var(--hairline)]"
            }`}
          >
            {k}
          </button>
        );
      })}
    </div>
  );
};

// ---------- QR mock (simple visual placeholder) ----------
const QrMock = () => (
  <svg viewBox="0 0 100 100" className="w-full h-full">
    <rect width="100" height="100" fill="#fff" />
    {/* corner anchors */}
    <rect x="6" y="6" width="22" height="22" fill="none" stroke="#0f172a" strokeWidth="4" />
    <rect x="12" y="12" width="10" height="10" fill="#0f172a" />
    <rect x="72" y="6" width="22" height="22" fill="none" stroke="#0f172a" strokeWidth="4" />
    <rect x="78" y="12" width="10" height="10" fill="#0f172a" />
    <rect x="6" y="72" width="22" height="22" fill="none" stroke="#0f172a" strokeWidth="4" />
    <rect x="12" y="78" width="10" height="10" fill="#0f172a" />
    {/* dots */}
    {Array.from({ length: 50 }).map((_, i) => {
      const x = 32 + (i % 10) * 4;
      const y = 32 + Math.floor(i / 10) * 4;
      const on = (i * 73 + 17) % 5 < 3;
      if (!on) return null;
      return <rect key={i} x={x} y={y} width="3" height="3" fill="#0f172a" />;
    })}
  </svg>
);

// ---------- method pill ----------
const Pill = ({
  icon,
  label,
  active,
  onClick,
  shortcut,
}: {
  icon: IconName;
  label: string;
  active: boolean;
  onClick: () => void;
  shortcut?: string;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`relative px-1.5 py-3 rounded-2xl flex flex-col items-center gap-1.5 border font-semibold text-xs transition-colors ${
      active
        ? "bg-[var(--ink)] text-white border-transparent"
        : "bg-[var(--surface-2)] text-[var(--ink-2)] border-[var(--hairline)] hover:bg-white"
    }`}
  >
    <Icon name={icon} size={20} stroke={active ? 2 : 1.7} />
    <span>{label}</span>
    {shortcut && (
      <span
        className={`absolute top-1 right-1.5 text-[9px] font-mono ${
          active ? "text-white/60" : "text-[var(--ink-faint)]"
        }`}
      >
        {shortcut}
      </span>
    )}
  </button>
);

// ============================================================
// MAIN PANEL
// ============================================================
export default function PagoPanel({
  total,
  clienteLabel,
  itemsCount,
  onClose,
  onConfirm,
}: PagoPanelProps) {
  const [method, setMethod] = useState<Method>("efectivo");
  const [tipoComp, setTipoComp] = useState<TipoComprobante>("ticket");
  const [imprimir, setImprimir] = useState<boolean>(true);

  function changeTipoComp(t: TipoComprobante) {
    setTipoComp(t);
    if (t === "whatsapp") setImprimir(false);
    else setImprimir(true);
  }

  // ---- efectivo ----
  const [recibido, setRecibido] = useState<number>(total);
  const vuelto = recibido - total;

  // ---- tarjeta ----
  const [tarjetaTipo, setTarjetaTipo] = useState<TarjetaTipo>("debito");
  const [cuota, setCuota] = useState<number>(1);

  // ---- qr ----
  const [qrModo, setQrModo] = useState<QrModo>("qr");

  // ---- mixto ----
  const [pagos, setPagos] = useState<PagoEntry[]>([]);
  const totalMixto = useMemo(
    () => pagos.reduce((a, p) => a + p.monto, 0),
    [pagos]
  );
  const restanteMixto = Math.max(0, +(total - totalMixto).toFixed(2));
  const [mixtoMedio, setMixtoMedio] = useState<MedioPago>("efectivo");
  const [mixtoMonto, setMixtoMonto] = useState<string>("");

  const [error, setError] = useState<string | null>(null);

  const { available: mpAvailable, checked: mpChecked } = useMpAvailability();

  const qr = useMpQrPayment({
    onApproved: (r) => {
      if (method === "mixto") {
        addMixtoPago({
          medio: "mp_qr",
          monto: r.monto,
          mp_payment_id: r.mp_payment_id,
        });
      } else {
        onConfirm(
          [{ medio: "mp_qr", monto: r.monto, mp_payment_id: r.mp_payment_id }],
          { tipo_comprobante: tipoComp, imprimir }
        );
      }
    },
  });
  const point = useMpPointPayment({
    onApproved: (r) => {
      if (method === "mixto") {
        addMixtoPago({
          medio: "mp_point",
          monto: r.monto,
          mp_payment_id: r.mp_payment_id,
        });
      } else {
        onConfirm(
          [
            {
              medio: "mp_point",
              monto: r.monto,
              mp_payment_id: r.mp_payment_id,
            },
          ],
          { tipo_comprobante: tipoComp, imprimir }
        );
      }
    },
  });

  const asyncRunning = qr.status === "pending" || point.status === "pending";
  const asyncType: "qr" | "point" | null =
    qr.status === "pending"
      ? "qr"
      : point.status === "pending"
        ? "point"
        : null;

  const mpFlash =
    qr.status === "timeout"
      ? "QR: timeout (5 min) sin confirmación"
      : qr.status === "rejected"
        ? `QR rechazado${qr.error ? `: ${qr.error}` : ""}`
        : qr.status === "error"
          ? `QR: ${qr.error || "error"}`
          : point.status === "timeout"
            ? "Point: timeout (5 min) sin confirmación"
            : point.status === "rejected"
              ? `Point rechazado${point.error ? `: ${point.error}` : ""}`
              : point.status === "error"
                ? `Point: ${point.error || "error"}`
                : null;

  const clearMpFlash = () => {
    qr.reset();
    point.reset();
  };

  // ---- handlers ----
  function addMixtoPago(p: Pago) {
    setPagos((prev) => [
      ...prev,
      {
        ...p,
        id: crypto.randomUUID(),
        label: labelMedio(p.medio),
      },
    ]);
    setMixtoMonto("");
    setError(null);
  }

  function removeMixtoPago(id: string) {
    setPagos((prev) => prev.filter((p) => p.id !== id));
  }

  function onNumKey(k: string) {
    if (k === "←") setRecibido((v) => Math.floor(v / 10));
    else if (k === "C") setRecibido(0);
    else if (k === "OK") confirmar();
    else if (k === ".") return;
    else if (k === "%") return;
    else if (k === "00") setRecibido((v) => v * 100);
    else if (!isNaN(parseInt(k))) setRecibido((v) => v * 10 + parseInt(k));
  }

  function confirmar() {
    setError(null);
    const extras: ConfirmExtras = {
      tipo_comprobante: tipoComp,
      imprimir,
    };
    if (method === "efectivo") {
      if (recibido < total) {
        setError(`Falta ${fmt(total - recibido)}`);
        return;
      }
      onConfirm([{ medio: "efectivo", monto: total }], extras);
      return;
    }
    if (method === "tarjeta") {
      onConfirm([{ medio: tarjetaTipo, monto: total }], extras);
      return;
    }
    if (method === "qr") {
      if (!mpAvailable) {
        setError("Mercado Pago no está configurado");
        return;
      }
      if (qrModo === "qr") qr.start(total);
      else point.start(total);
      return;
    }
    if (method === "mixto") {
      if (restanteMixto > 0.01) {
        setError(`Falta cobrar ${fmt(restanteMixto)}`);
        return;
      }
      onConfirm(
        pagos.map((p) => ({
          medio: p.medio,
          monto: p.monto,
          ...(p.mp_payment_id ? { mp_payment_id: p.mp_payment_id } : {}),
        })),
        extras
      );
    }
  }

  function agregarMixto() {
    const m = parseFloat(mixtoMonto);
    if (!Number.isFinite(m) || m <= 0) {
      setError("Ingresá un monto válido");
      return;
    }
    if (m > restanteMixto + 0.01 && mixtoMedio !== "efectivo") {
      setError(`No podés exceder el restante (${fmt(restanteMixto)})`);
      return;
    }
    if (mixtoMedio === "efectivo") {
      addMixtoPago({ medio: "efectivo", monto: Math.min(m, restanteMixto) });
      return;
    }
    if (mixtoMedio === "mp_qr") {
      if (!mpAvailable) {
        setError("MP no configurado");
        return;
      }
      qr.start(m);
      return;
    }
    if (mixtoMedio === "mp_point") {
      if (!mpAvailable) {
        setError("MP no configurado");
        return;
      }
      point.start(m);
      return;
    }
    addMixtoPago({ medio: mixtoMedio, monto: m });
  }

  function cancelarPendiente() {
    if (qr.status === "pending") qr.cancel();
    if (point.status === "pending") point.cancel();
  }

  // ---- shortcuts ----
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (asyncRunning) {
        if (e.key === "Escape") {
          e.preventDefault();
          cancelarPendiente();
        }
        return;
      }
      const tag = (e.target as HTMLElement | null)?.tagName;
      const inField = tag === "INPUT" || tag === "TEXTAREA";

      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === "Enter") {
        e.preventDefault();
        if (method === "mixto" && mixtoMonto && restanteMixto > 0) {
          agregarMixto();
        } else {
          confirmar();
        }
        return;
      }
      if (inField) return;
      const k = e.key.toLowerCase();
      if (k === "e") {
        e.preventDefault();
        setMethod("efectivo");
      } else if (k === "t") {
        e.preventDefault();
        setMethod("tarjeta");
      } else if (k === "q") {
        e.preventDefault();
        setMethod("qr");
      } else if (k === "m") {
        e.preventDefault();
        setMethod("mixto");
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    method,
    recibido,
    total,
    asyncRunning,
    pagos,
    mixtoMonto,
    mixtoMedio,
    restanteMixto,
    tarjetaTipo,
    qrModo,
    mpAvailable,
  ]);

  // Confirm button label/state
  const canConfirm =
    method === "efectivo"
      ? recibido >= total
      : method === "mixto"
        ? restanteMixto <= 0.01
        : true;
  const ctaLabel =
    method === "qr"
      ? qrModo === "qr"
        ? `Generar QR ${fmt(total)}`
        : `Cobrar en Point ${fmt(total)}`
      : `Confirmar ${fmt(total)}`;

  // ============================================================
  // RENDER
  // ============================================================
  return (
    <aside
      className="w-[460px] flex-shrink-0 bg-white flex flex-col rounded-3xl border border-[var(--hairline)] shadow-[var(--shadow-lg)] m-3.5 ml-0 relative z-10"
      style={{ fontFamily: "var(--font-sans)" }}
    >
      {/* MP busy overlay */}
      {asyncRunning && (
        <div className="absolute inset-0 z-20 bg-white/95 rounded-3xl flex flex-col items-center justify-center p-6 text-center">
          <div className="w-24 h-24 rounded-full bg-[var(--mint-tint)] text-[var(--mint-deep)] flex items-center justify-center relative">
            <Icon
              name={asyncType === "qr" ? "qr" : "card"}
              size={40}
              stroke={1.8}
            />
            <span className="absolute inset-[-8px] rounded-full border-2 border-[var(--mint)] opacity-40 pos-pulse-ring" />
          </div>
          <div className="mt-5 font-bold text-lg text-[var(--ink)]">
            {asyncType === "qr"
              ? "Esperando que el cliente escanee"
              : "Insertá la tarjeta en el Point"}
          </div>
          <div className="mt-1.5 text-sm text-[var(--ink-muted)]">
            {fmt(asyncType === "qr" ? qr.monto : point.monto)} · timeout 5 min
          </div>
          <button
            type="button"
            onClick={cancelarPendiente}
            className="mt-6 px-6 py-3 rounded-xl bg-[var(--red)] hover:opacity-90 text-white font-bold text-sm"
          >
            Cancelar cobro
          </button>
        </div>
      )}

      {/* Header */}
      <div className="px-6 pt-5 pb-3.5 border-b border-[var(--hairline)]">
        <div className="flex items-center gap-2.5">
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-xl bg-[var(--bg-sunken)] text-[var(--ink-2)] flex items-center justify-center"
            title="Volver"
          >
            <Icon name="back" size={18} />
          </button>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] font-bold text-[var(--ink-faint)] tracking-wider uppercase">
              Cobrar
            </div>
            <div className="text-base font-bold truncate">
              {clienteLabel} · {itemsCount} {itemsCount === 1 ? "ítem" : "ítems"}
            </div>
          </div>
          <kbd className="px-2 py-1 bg-[var(--bg-sunken)] rounded-md text-[11px] font-mono text-[var(--ink-muted)] font-semibold">
            Esc
          </kbd>
        </div>

        <div className="mt-4 flex items-baseline justify-between">
          <span className="text-xs font-bold text-[var(--ink-faint)] tracking-wider uppercase">
            Total
          </span>
          <span className="text-5xl font-extrabold tracking-tight leading-none">
            {fmt(total)}
          </span>
        </div>

        {method === "mixto" && (
          <div className="mt-3.5">
            <div className="flex h-2 rounded-full overflow-hidden bg-[var(--bg-sunken)]">
              {pagos.map((p, i) => (
                <div
                  key={p.id}
                  style={{
                    width: `${Math.min(100, (p.monto / total) * 100)}%`,
                    background:
                      i % 2 === 0 ? "var(--mint)" : "#a78bfa",
                  }}
                />
              ))}
            </div>
            <div className="flex justify-between mt-1.5 text-[11px] text-[var(--ink-muted)] font-semibold">
              <span>Cubierto {fmt(totalMixto)}</span>
              <span
                className={
                  restanteMixto > 0
                    ? "text-[var(--amber)]"
                    : "text-[var(--mint-deep)]"
                }
              >
                {restanteMixto > 0
                  ? `Falta ${fmt(restanteMixto)}`
                  : "Listo"}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Tipo de comprobante + imprimir */}
      <div className="px-5 pt-4 pb-1 flex items-center gap-3">
        <div className="flex flex-1 bg-[var(--bg-sunken)] rounded-2xl p-1 gap-0.5">
          {(
            [
              { id: "ticket", label: "Ticket" },
              { id: "factura_b", label: "Factura B" },
              { id: "whatsapp", label: "WhatsApp" },
            ] as { id: TipoComprobante; label: string }[]
          ).map((t) => {
            const active = tipoComp === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => changeTipoComp(t.id)}
                className={`flex-1 py-2 rounded-xl text-xs font-bold transition-colors ${
                  active
                    ? "bg-white text-[var(--ink)] shadow-[var(--shadow-sm)]"
                    : "text-[var(--ink-muted)] hover:text-[var(--ink-2)]"
                }`}
              >
                {t.label}
              </button>
            );
          })}
        </div>
        <button
          type="button"
          onClick={() => setImprimir((v) => !v)}
          disabled={tipoComp === "whatsapp"}
          className={`flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-bold border transition-colors ${
            imprimir
              ? "bg-[var(--mint-tint)] text-[var(--mint-deep)] border-[var(--mint-soft)]"
              : "bg-white text-[var(--ink-faint)] border-[var(--hairline)]"
          } ${tipoComp === "whatsapp" ? "opacity-50 cursor-not-allowed" : ""}`}
          title={
            tipoComp === "whatsapp"
              ? "WhatsApp no imprime ticket"
              : imprimir
                ? "Click para no imprimir"
                : "Click para imprimir"
          }
        >
          <span
            className={`inline-block w-7 h-4 rounded-full relative transition-colors ${
              imprimir ? "bg-[var(--mint)]" : "bg-[var(--ink-faint)]"
            }`}
          >
            <span
              className={`absolute top-0.5 w-3 h-3 bg-white rounded-full transition-all ${
                imprimir ? "left-3" : "left-0.5"
              }`}
            />
          </span>
          Imprimir
        </button>
      </div>

      {/* Method pills */}
      <div className="px-4 pt-3.5 grid grid-cols-4 gap-2">
        <Pill
          icon="banknote"
          label="Efectivo"
          active={method === "efectivo"}
          onClick={() => setMethod("efectivo")}
          shortcut="E"
        />
        <Pill
          icon="card"
          label="Tarjeta"
          active={method === "tarjeta"}
          onClick={() => setMethod("tarjeta")}
          shortcut="T"
        />
        <Pill
          icon="qr"
          label="QR / MP"
          active={method === "qr"}
          onClick={() => setMethod("qr")}
          shortcut="Q"
        />
        <Pill
          icon="split"
          label="Mixto"
          active={method === "mixto"}
          onClick={() => setMethod("mixto")}
          shortcut="M"
        />
      </div>

      {/* Body */}
      <div className="flex-1 overflow-auto px-5 pt-4 pb-1">
        {/* MP not available banner */}
        {mpChecked &&
          !mpAvailable &&
          (method === "qr" || method === "mixto") && (
            <div className="mb-3 rounded-lg bg-[var(--amber-soft)] text-[var(--ink-2)] border border-[var(--amber)]/40 px-3 py-2 text-xs font-semibold">
              ⚠️ Mercado Pago no está configurado.
            </div>
          )}

        {mpFlash && (
          <div className="mb-3 rounded-lg bg-[var(--red-soft)] border border-[var(--red)]/30 px-3 py-2 text-xs text-[var(--red)] font-semibold flex items-center justify-between gap-2">
            <span>{mpFlash}</span>
            <button
              type="button"
              onClick={clearMpFlash}
              className="text-[var(--red)] font-bold"
            >
              ✕
            </button>
          </div>
        )}

        {method === "efectivo" && (
          <EfectivoBody
            recibido={recibido}
            total={total}
            setRecibido={setRecibido}
            vuelto={vuelto}
            onKey={onNumKey}
          />
        )}
        {method === "tarjeta" && (
          <TarjetaBody
            tipo={tarjetaTipo}
            setTipo={setTarjetaTipo}
            cuota={cuota}
            setCuota={setCuota}
            total={total}
          />
        )}
        {method === "qr" && (
          <QrBody
            modo={qrModo}
            setModo={setQrModo}
            total={total}
            mpAvailable={mpAvailable}
          />
        )}
        {method === "mixto" && (
          <MixtoBody
            pagos={pagos}
            removePago={removeMixtoPago}
            restante={restanteMixto}
            mixtoMedio={mixtoMedio}
            setMixtoMedio={setMixtoMedio}
            mixtoMonto={mixtoMonto}
            setMixtoMonto={setMixtoMonto}
            onAgregar={agregarMixto}
            mpAvailable={mpAvailable}
          />
        )}
      </div>

      {/* Footer */}
      <div className="px-5 pb-4 pt-2">
        {error && (
          <div className="mb-2 text-xs text-[var(--red)] font-semibold">
            {error}
          </div>
        )}
        <button
          type="button"
          onClick={confirmar}
          disabled={!canConfirm}
          className={`w-full py-4 rounded-2xl font-extrabold text-base flex items-center justify-center gap-2.5 transition-opacity ${
            canConfirm
              ? "bg-[var(--mint)] text-white shadow-[0_12px_28px_-10px_rgba(31,200,155,0.65)] hover:opacity-95"
              : "bg-[var(--ink-faint)] text-white opacity-60 cursor-not-allowed"
          }`}
        >
          {ctaLabel}
          <Icon name="arrow-right" size={20} />
        </button>
      </div>
    </aside>
  );
}

// ============================================================
// BODIES
// ============================================================

function EfectivoBody({
  recibido,
  total,
  setRecibido,
  vuelto,
  onKey,
}: {
  recibido: number;
  total: number;
  setRecibido: (n: number) => void;
  vuelto: number;
  onKey: (k: string) => void;
}) {
  return (
    <div>
      <div className="text-[11px] font-bold text-[var(--ink-muted)] tracking-wider uppercase mb-2">
        Recibí
      </div>
      <div className="px-5 py-3.5 border-2 border-[var(--mint)] rounded-2xl bg-[var(--mint-tint)] flex items-baseline justify-between">
        <span className="text-3xl font-extrabold tabular-nums">
          {fmt(recibido)}
        </span>
        <span className="text-[10px] font-bold text-[var(--mint-deep)] tracking-wider">
          EFECTIVO
        </span>
      </div>
      <div className="flex gap-1.5 mt-3 flex-wrap">
        {SUGGEST_BILLS.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setRecibido(s)}
            className={`px-3 py-1.5 rounded-full font-bold text-xs border ${
              recibido === s
                ? "bg-[var(--ink)] text-white border-transparent"
                : "bg-[var(--surface-2)] text-[var(--ink-2)] border-[var(--hairline)]"
            }`}
          >
            {fmt(s)}
          </button>
        ))}
        <button
          type="button"
          onClick={() => setRecibido(total)}
          className="px-3 py-1.5 rounded-full font-bold text-xs bg-[var(--surface-2)] text-[var(--ink-2)] border border-[var(--hairline)]"
        >
          Justo
        </button>
      </div>

      <div
        className={`mt-3.5 px-4 py-3 rounded-2xl flex items-center justify-between ${
          vuelto < 0
            ? "bg-[var(--red-soft)] border border-[var(--red)]/30"
            : "bg-[var(--bg-sunken)]"
        }`}
      >
        <span
          className={`text-[11px] font-bold tracking-wider uppercase ${
            vuelto < 0 ? "text-[var(--red)]" : "text-[var(--ink-muted)]"
          }`}
        >
          {vuelto < 0 ? "Falta" : "Vuelto"}
        </span>
        <span
          className={`text-2xl font-extrabold tabular-nums ${
            vuelto < 0 ? "text-[var(--red)]" : "text-[var(--ink)]"
          }`}
        >
          {fmt(Math.abs(vuelto))}
        </span>
      </div>

      <div className="mt-4">
        <Numpad onKey={onKey} />
      </div>
    </div>
  );
}

function TarjetaBody({
  tipo,
  setTipo,
  cuota,
  setCuota,
  total,
}: {
  tipo: TarjetaTipo;
  setTipo: (t: TarjetaTipo) => void;
  cuota: number;
  setCuota: (c: number) => void;
  total: number;
}) {
  return (
    <div>
      <div className="text-[11px] font-bold text-[var(--ink-muted)] tracking-wider uppercase mb-2">
        Tipo de tarjeta
      </div>
      <div className="grid grid-cols-2 gap-2">
        {(["debito", "credito"] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTipo(t)}
            className={`py-3 rounded-2xl font-bold text-sm border ${
              tipo === t
                ? "bg-[var(--ink)] text-white border-transparent"
                : "bg-[var(--surface-2)] text-[var(--ink-2)] border-[var(--hairline)]"
            }`}
          >
            {t === "debito" ? "Débito" : "Crédito"}
          </button>
        ))}
      </div>

      {tipo === "credito" && (
        <>
          <div className="text-[11px] font-bold text-[var(--ink-muted)] tracking-wider uppercase mb-2 mt-4">
            Cuotas
          </div>
          <div className="grid grid-cols-3 gap-2">
            {CUOTAS.map((c) => (
              <button
                key={c.q}
                type="button"
                onClick={() => setCuota(c.q)}
                className={`py-3 rounded-2xl flex flex-col items-center gap-0.5 border ${
                  cuota === c.q
                    ? "bg-[var(--ink)] text-white border-transparent"
                    : "bg-[var(--surface-2)] text-[var(--ink-2)] border-[var(--hairline)]"
                }`}
              >
                <span className="font-extrabold text-lg">{c.q}x</span>
                <span
                  className={`text-[10px] ${
                    cuota === c.q ? "text-white/70" : "text-[var(--ink-muted)]"
                  }`}
                >
                  {c.sub}
                </span>
              </button>
            ))}
          </div>
          <div className="mt-3 px-4 py-3 rounded-2xl bg-[var(--surface-2)] border border-[var(--hairline)] flex items-baseline justify-between">
            <span className="text-xs text-[var(--ink-muted)] font-semibold">
              Cada cuota
            </span>
            <span className="text-2xl font-extrabold tabular-nums">
              {fmt(total / cuota)}
            </span>
          </div>
        </>
      )}

      <div className="mt-4 px-5 py-5 rounded-2xl bg-[var(--surface-2)] border border-[var(--hairline)] flex flex-col items-center text-center">
        <div className="w-20 h-20 rounded-full bg-[var(--mint-tint)] text-[var(--mint-deep)] flex items-center justify-center relative">
          <Icon name="card" size={34} stroke={1.8} />
          <span className="absolute inset-[-6px] rounded-full border-2 border-[var(--mint)] opacity-30 pos-pulse-ring" />
        </div>
        <div className="mt-3 font-bold text-sm">Acercá o insertá la tarjeta</div>
        <div className="text-xs text-[var(--ink-muted)] mt-0.5">
          Posnet · Ingenico Move/2500
        </div>
      </div>
    </div>
  );
}

function QrBody({
  modo,
  setModo,
  total,
  mpAvailable,
}: {
  modo: QrModo;
  setModo: (m: QrModo) => void;
  total: number;
  mpAvailable: boolean;
}) {
  return (
    <div>
      <div className="text-[11px] font-bold text-[var(--ink-muted)] tracking-wider uppercase mb-2">
        Modo Mercado Pago
      </div>
      <div className="grid grid-cols-2 gap-2 mb-4">
        {(
          [
            { id: "qr", label: "QR (cliente escanea)" },
            { id: "point", label: "Point (POS físico)" },
          ] as const
        ).map((m) => (
          <button
            key={m.id}
            type="button"
            onClick={() => setModo(m.id)}
            disabled={!mpAvailable}
            className={`py-3 px-2 rounded-2xl font-bold text-xs border ${
              modo === m.id
                ? "bg-[var(--ink)] text-white border-transparent"
                : "bg-[var(--surface-2)] text-[var(--ink-2)] border-[var(--hairline)]"
            } ${!mpAvailable ? "opacity-50 cursor-not-allowed" : ""}`}
          >
            {m.label}
          </button>
        ))}
      </div>

      {modo === "qr" ? (
        <div className="flex flex-col items-center text-center">
          <div className="w-52 h-52 bg-white rounded-3xl p-4 border border-[var(--hairline)] shadow-[var(--shadow-md)]">
            <QrMock />
          </div>
          <div className="mt-3 font-bold text-sm">
            Escaneá con Mercado Pago
          </div>
          <div className="text-xs text-[var(--ink-muted)] mt-0.5">
            Cobro por {fmt(total)} · esperando confirmación al generar
          </div>
        </div>
      ) : (
        <div className="px-5 py-6 rounded-2xl bg-[var(--surface-2)] border border-[var(--hairline)] flex flex-col items-center text-center">
          <div className="w-20 h-20 rounded-full bg-[var(--mint-tint)] text-[var(--mint-deep)] flex items-center justify-center">
            <Icon name="card" size={34} stroke={1.8} />
          </div>
          <div className="mt-3 font-bold text-sm">Cobrar en Point físico</div>
          <div className="text-xs text-[var(--ink-muted)] mt-0.5">
            Al confirmar se envía el cobro al Point conectado
          </div>
        </div>
      )}
    </div>
  );
}

const MIXTO_MEDIOS: { key: MedioPago; label: string; needsMp?: boolean }[] = [
  { key: "efectivo", label: "Efectivo" },
  { key: "debito", label: "Débito" },
  { key: "credito", label: "Crédito" },
  { key: "transferencia", label: "Transferencia" },
  { key: "cta_cte", label: "Cta. Cte." },
  { key: "mp_qr", label: "MP QR", needsMp: true },
  { key: "mp_point", label: "MP Point", needsMp: true },
];

function MixtoBody({
  pagos,
  removePago,
  restante,
  mixtoMedio,
  setMixtoMedio,
  mixtoMonto,
  setMixtoMonto,
  onAgregar,
  mpAvailable,
}: {
  pagos: PagoEntry[];
  removePago: (id: string) => void;
  restante: number;
  mixtoMedio: MedioPago;
  setMixtoMedio: (m: MedioPago) => void;
  mixtoMonto: string;
  setMixtoMonto: (v: string) => void;
  onAgregar: () => void;
  mpAvailable: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <div>
      <div className="text-[11px] font-bold text-[var(--ink-muted)] tracking-wider uppercase mb-2">
        Pagos parciales
      </div>

      {pagos.length === 0 && (
        <div className="text-center py-4 text-xs text-[var(--ink-faint)]">
          Agregá pagos parciales hasta cubrir {fmt(restante)}
        </div>
      )}

      <div className="flex flex-col gap-2">
        {pagos.map((p) => (
          <div
            key={p.id}
            className="flex items-center gap-3 px-3.5 py-3 rounded-2xl bg-[var(--surface-2)] border border-[var(--hairline)]"
          >
            <div className="w-10 h-10 rounded-xl bg-white border border-[var(--hairline)] flex items-center justify-center text-[var(--ink-2)]">
              <Icon
                name={
                  p.medio === "efectivo"
                    ? "banknote"
                    : p.medio === "mp_qr"
                      ? "qr"
                      : p.medio === "transferencia"
                        ? "phone"
                        : p.medio === "cta_cte"
                          ? "clipboard"
                          : "card"
                }
                size={18}
              />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-sm">{p.label}</div>
              {p.mp_payment_id && (
                <div className="text-[10px] text-[var(--ink-faint)] font-mono truncate">
                  MP #{p.mp_payment_id}
                </div>
              )}
            </div>
            <div className="text-lg font-extrabold tabular-nums">
              {fmt(p.monto)}
            </div>
            <button
              type="button"
              onClick={() => removePago(p.id)}
              className="w-7 h-7 rounded-lg text-[var(--ink-faint)] hover:text-[var(--red)] flex items-center justify-center"
              title="Quitar"
            >
              <Icon name="trash" size={16} />
            </button>
          </div>
        ))}
      </div>

      {restante > 0 && (
        <div className="mt-3 rounded-2xl border border-dashed border-[var(--hairline-2)] p-3 space-y-2">
          <div className="grid grid-cols-3 gap-1.5">
            {MIXTO_MEDIOS.map((m) => {
              const disabled = !!m.needsMp && !mpAvailable;
              const active = mixtoMedio === m.key;
              return (
                <button
                  key={m.key}
                  type="button"
                  disabled={disabled}
                  onClick={() => setMixtoMedio(m.key)}
                  className={`py-2 rounded-xl text-[11px] font-bold border ${
                    active
                      ? "bg-[var(--ink)] text-white border-transparent"
                      : "bg-white text-[var(--ink-2)] border-[var(--hairline)]"
                  } ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
                  title={disabled ? "MP no configurado" : undefined}
                >
                  {m.label}
                </button>
              );
            })}
          </div>
          <div className="flex gap-2">
            <input
              ref={inputRef}
              type="number"
              step="0.01"
              min="0"
              value={mixtoMonto}
              onChange={(e) => setMixtoMonto(e.target.value)}
              placeholder={fmt(restante)}
              className="flex-1 px-3 py-2.5 rounded-xl border border-[var(--hairline)] bg-white text-base font-bold tabular-nums focus:outline-none focus:ring-2 focus:ring-[var(--mint)]"
            />
            <button
              type="button"
              onClick={() => setMixtoMonto(restante.toFixed(2))}
              className="px-3 py-2.5 rounded-xl bg-[var(--surface-2)] border border-[var(--hairline)] text-xs font-bold text-[var(--ink-2)]"
            >
              Resto
            </button>
            <button
              type="button"
              onClick={onAgregar}
              className="px-4 py-2.5 rounded-xl bg-[var(--ink)] text-white text-xs font-bold flex items-center gap-1.5"
            >
              <Icon name="plus" size={14} /> Agregar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
